import { memo } from "react";

import classnames from "classnames";
import { isPresent } from "neetocist";
import withT from "neetocommons/react-utils/withT";
import { EditorContent } from "neetoeditor";
import { Typography, Button } from "neetoui";
import PropTypes from "prop-types";

import AvatarPlaceholder from "assets/images/avatar-placeholder";

const MemoizedEditorContent = memo(EditorContent);

const EmailPreview = withT(
  ({
    t,
    to = [],
    from,
    subject,
    body,
    actionButtonText,
    logo,
    className = "",
    customContent,
  }) => (
    <div
      data-testid="email-preview-card"
      className={classnames(
        "neeto-ui-bg-white neeto-ui-rounded-lg neeto-ui-theme--light neeto-notification-email-wrapper__preview relative flex flex-1 flex-col px-6 py-8",
        className
      )}
    >
      <div className="neeto-molecules-email-preview-ribbon">
        {t("neetoMolecules.emailPreview.previewMessage")}
      </div>
      <div className="w-full pb-4">
        <Typography
          className="neeto-ui-text-gray-800 min-w-0 grow space-x-2 break-words"
          data-testid="email-subject-preview"
          lineHeight="normal"
          style="h4"
        >
          <span>
            {subject || (
              <span className="neeto-ui-bg-gray-300 neeto-ui-rounded-full h-2 w-40" />
            )}
          </span>
        </Typography>
        <div className="mt-4 flex items-center gap-2">
          <div className="shrink-0">
            <AvatarPlaceholder />
          </div>
          <div className="neeto-ui-flex-col flex gap-1">
            {isPresent(from) ? (
              <Typography style="h6" weight="medium">
                {from}
              </Typography>
            ) : (
              <div className="flex gap-2">
                <span className="neeto-ui-bg-gray-100 neeto-ui-rounded-full h-2 w-24" />
                <span className="neeto-ui-bg-gray-100 neeto-ui-rounded-full h-2 w-24" />
              </div>
            )}
            {isPresent(to) ? (
              <Typography
                className="neeto-ui-text-gray-500 line-clamp-1"
                data-testid="email-to-block"
                style="nano"
              >
                {to.join(", ")}
              </Typography>
            ) : (
              <span
                className="neeto-ui-bg-gray-100 neeto-ui-rounded-full h-2 w-16"
                data-testid="email-to-block"
              />
            )}
          </div>
        </div>
      </div>
      <div
        className="neeto-ui-rounded neeto-ui-bg-gray-100 flex flex-1 flex-col justify-between gap-y-6 px-6 py-4 text-base leading-relaxed"
        data-testid="email-body-preview-container"
      >
        <div className="neeto-ui-bg-white w-full p-6 md:p-8 2xl:mx-auto 2xl:max-w-lg">
          {body ? (
            <MemoizedEditorContent
              className="whitespace-normal"
              content={body}
            />
          ) : (
            <div className="flex flex-col gap-y-4">
              <div className="neeto-ui-bg-gray-300 neeto-ui-rounded-full h-2 w-48 max-w-full md:w-80" />
              <div className="neeto-ui-bg-gray-300 neeto-ui-rounded-full h-2 w-40 max-w-full md:w-72" />
              <div className="neeto-ui-bg-gray-300 neeto-ui-rounded-full h-2 w-48 max-w-full md:w-80" />
            </div>
          )}
          {customContent && customContent}
          {actionButtonText && (
            <div className="mt-6 flex w-full items-center justify-center">
              <Button
                className="pointer-events-none"
                label={actionButtonText}
              />
            </div>
          )}
          {logo && (
            <img
              alt={t("neetoMolecules.emailPreview.logoAltText")}
              className="h-20 w-36 object-cover"
              data-testid="company-logo-preview"
              src={logo}
            />
          )}
        </div>
      </div>
    </div>
  )
);

EmailPreview.propTypes = {
  /**
   * The value of the send-to field.
   */
  to: PropTypes.arrayOf(PropTypes.string),
  /**
   * The value of the send-from field.
   */
  from: PropTypes.string,
  /**
   * The subject of the email.
   */
  subject: PropTypes.string,
  /**
   * The body of the email.
   */
  body: PropTypes.string,
  /**
   * The text to be shown in the action button.
   */
  actionButtonText: PropTypes.string,
  /**
   * Source link for the company logo.
   */
  logo: PropTypes.string,
  /**
   * To pass custom content that will be rendered above the action button.
   */
  customContent: PropTypes.string,
};

export default EmailPreview;
